import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDownToLine, Cable, Plus, Radar, RefreshCw, Save, Shuffle } from 'lucide-react';
import { eventNotificationsOn } from '@simpleacs/shared';
import type { AutoAuthorizeWindow, JobHandle, Olt, OltUnseenEvents, Page } from '@simpleacs/shared';
import { OnuMigrationDialog } from './olt/OnuMigrationDialog';
import { MigrateDialog } from './smartolt/MigrateDialog';
import { DisabledPill } from '../components/OutOfService';
import { api } from '../lib/api';
import { runJob } from '../lib/jobs';
import { formatRelative, formatUptime } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

const STATUS_TONE = { up: 'success', down: 'danger', unknown: 'neutral' } as const;

export function OltsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeOrg, can } = useSession();

  const oltsQuery = useQuery({
    queryKey: ['olts', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
    refetchInterval: 30_000,
  });

  const probe = useMutation({
    mutationFn: (oltId: string) => api.post(`/olts/${encodeURIComponent(oltId)}/probe`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['olts'] }),
  });

  // Fleet-wide actions fan out per OLT (the backend serialises each chassis
  // through its own queue), so one slow or unreachable OLT never blocks the rest.
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  /** Which chassis has the migration dialog open. */
  const [migrating, setMigrating] = useState<Olt | null>(null);
  /** The SmartOLT migration wizard — reachable from here as well as Settings. */
  const [smartolt, setSmartolt] = useState(false);

  /**
   * Which OLTs are armed to auto-authorise migrations right now.
   *
   * Read for the whole fleet in one request and shown ON the row, because
   * "which chassis is currently allowed to authorise subscribers by itself" is
   * exactly the thing that must never need a click to find out. It expires by
   * its own stored timestamp, so this is a poll rather than a subscription —
   * a page left open cannot keep a window looking armed after it has run out.
   */
  const windowsQuery = useQuery({
    queryKey: ['auto-authorize'],
    queryFn: () => api.get<{ items: AutoAuthorizeWindow[] }>('/olts/auto-authorize'),
    refetchInterval: 30_000,
    enabled: can('olt.read'),
  });
  const armedByOlt = new Map(
    (windowsQuery.data?.items ?? []).filter((w) => w.active).map((w) => [w.oltId, w]),
  );

  /**
   * ONU events on each chassis that THIS user has not looked at yet.
   *
   * This replaces four amber banners that used to stand above the table, one
   * per OLT, every sweep — "78 ONU state change(s) recorded — see the events
   * list for the cause of each." A number, and then an instruction to go
   * somewhere else to find out what it meant, permanently occupying the top of
   * the page. The number now sits in the row it is about and is a link to the
   * thing it is telling you to look at.
   *
   * One request for the fleet, like the auto-authorise windows above, because a
   * list of four chassis must not become four round trips. Per user rather than
   * global: one operator reading gachie1's events must not blank the count for
   * everybody else on the team.
   */
  const unseenQuery = useQuery({
    queryKey: ['olts', activeOrg?.id ?? 'all', 'unseen-events'],
    queryFn: () => api.get<{ items: OltUnseenEvents[] }>('/olts/unseen-events'),
    refetchInterval: 30_000,
    enabled: can('olt.read'),
  });
  const unseenByOlt = new Map(
    (unseenQuery.data?.items ?? []).map((r) => [r.oltId, r.unseen] as const),
  );

  const resyncAll = useMutation({
    mutationFn: async () => {
      // Serial, not parallel: two OLTs can share one public IP behind a NAT
      // (both Huawei chassis do), and syncing them at once contends on that NAT
      // and times columns out — the very warning this is meant to clear. One at
      // a time each sync completes cleanly and its stale warning clears.
      let failed = 0;
      let ntpPushed = 0;
      let ntpFailed = 0;
      for (const o of inService) {
        try {
          // Await the JOB to completion, not just the enqueue handle — that is
          // what keeps the loop serial and preserves the NAT guard below. A bare
          // post now returns instantly and would fire every chassis at once.
          await runJob(await api.post<JobHandle>(`/olts/${encodeURIComponent(o.id)}/sync`));
        } catch {
          failed += 1;
        }

        /**
         * And the time servers, on the OLTs that have some recorded — ADDED,
         * never replacing.
         *
         * `replace: false` is load-bearing. A per-OLT Sync on the Time card
         * replaces the chassis's list with the platform's, which is what an
         * operator means when they press that button on one chassis. This
         * button is about inventory across the whole fleet, and a removal from
         * four live chassis as a side effect of "Resync all" is exactly the
         * kind of thing nobody would find out about until a clock drifted.
         *
         * An OLT SimpleACS holds no servers for is left alone. A push that
         * fails is counted rather than thrown: one unreachable chassis should
         * not stop the other three from being brought into step, which is the
         * whole point of a fleet-wide action.
         */
        if ((o.ntpServers?.length ?? 0) > 0) {
          try {
            await api.post(`/olts/${encodeURIComponent(o.id)}/time/ntp/sync`, { replace: false });
            ntpPushed += 1;
          } catch {
            ntpFailed += 1;
          }
        }

        // Refresh as each finishes so its warning clears progressively.
        await queryClient.invalidateQueries({ queryKey: ['olts'] });
      }
      return { total: inService.length, failed, ntpPushed, ntpFailed };
    },
    onSuccess: ({ total, failed, ntpPushed, ntpFailed }) => {
      const synced = failed
        ? `Resynced ${total - failed}/${total} OLTs · ${failed} failed`
        : `Resynced all ${total} OLTs`;
      // Only mentioned when it happened, so an operator who has set no time
      // servers never reads about a step that did not run.
      const ntp =
        ntpPushed + ntpFailed === 0
          ? ''
          : ntpFailed
            ? ` · time servers written to ${ntpPushed}, refused by ${ntpFailed}`
            : ` · time servers written to ${ntpPushed}`;
      setBulkMsg(synced + ntp);
      queryClient.invalidateQueries({ queryKey: ['olts'] });
    },
    onError: (err) => setBulkMsg(err instanceof Error ? err.message : 'Resync failed'),
  });

  const saveAll = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        inService.map((o) =>
          api.post(`/olts/${encodeURIComponent(o.id)}/save-config`, { dryRun: false }),
        ),
      );
      return { total: results.length, failed: results.filter((r) => r.status === 'rejected').length };
    },
    onSuccess: ({ total, failed }) => {
      setBulkMsg(
        failed
          ? `Saved config on ${total - failed}/${total} OLTs · ${failed} failed`
          : `Wrote config to flash on all ${total} OLTs`,
      );
      queryClient.invalidateQueries({ queryKey: ['olts'] });
    },
    onError: (err) => setBulkMsg(err instanceof Error ? err.message : 'Save failed'),
  });

  const bulkBusy = resyncAll.isPending || saveAll.isPending;

  const olts = oltsQuery.data?.items ?? [];
  /**
   * Whether the Events column exists at all on this screen.
   *
   * An OLT can be silenced individually — see `eventNotificationsOn`, where
   * anything but an explicit `false` is a chassis that has never been
   * quietened. When only some are, the column stays and the silenced rows show
   * a dash: the alternative, omitting the `<td>` on those rows alone, slides
   * every later cell one column left and puts uptime under "Events".
   *
   * When EVERY visible OLT is silenced there is nothing left for the column to
   * say, and a header standing over a run of dashes reads as a table that
   * failed to load rather than as a preference somebody set. So the header and
   * all of its cells go together, which keeps the row consistent either way.
   * It comes straight back the moment one OLT is switched on again, or when a
   * filter brings a noisy chassis into view.
   */
  const showEventsColumn = olts.some(eventNotificationsOn);
  /**
   * The chassis the two fleet-wide buttons below are allowed to act on.
   *
   * A disabled OLT is out of scope for both, and for different reasons that
   * land in the same place: "Resync all" is a sweep, which a chassis taken out
   * of service is not supposed to get, and "Save / write all" is a write to
   * flash, which it is definitely not supposed to get. The API refuses both on
   * its own now, so this is no longer the only guard — but it is still the one
   * that makes the button say how many chassis it left out, instead of firing
   * requests it knows will come back refused.
   */
  const inService = olts.filter((o) => !o.disabledAt);
  const disabledCount = olts.length - inService.length;
  const counts = {
    total: olts.length,
    up: olts.filter((o) => o.status === 'up').length,
    // Subscriber totals cover the chassis that are still being read. A
    // disabled OLT is not polled, so its counts are frozen at the last sweep
    // and would keep a decommissioned chassis in the fleet figure for ever.
    // The chassis COUNT above still includes it — it is still a chassis, and
    // its row is right there marked out of service.
    onus: inService.reduce((sum, o) => sum + o.onuCount, 0),
    online: inService.reduce((sum, o) => sum + o.onuOnlineCount, 0),
  };
  /** The OLTs a fleet resync would write time servers to. */
  const withNtp = inService.filter((o) => (o.ntpServers?.length ?? 0) > 0);
  /** Named in both confirmations, so nobody counts the rows and finds one short. */
  const skipping =
    disabledCount === 0
      ? ''
      : ` ${disabledCount === 1 ? 'One chassis is' : `${disabledCount} chassis are`} out of service in SimpleACS and left out.`;

  return (
    <>
      <PageHeader
        title="OLTs"
        description="ZTE and Huawei line terminals, polled over SNMP and configured over the CLI."
        actions={
          can('olt.manage') ? (
            <>
              {/* Secondary beside the one primary: the OLTs a migration brings
                  still finish in the onboarding wizard, credentials and all. */}
              <Button icon={<ArrowDownToLine className="size-4" />} onClick={() => setSmartolt(true)}>
                Migrate from SmartOLT
              </Button>
              <Button
                variant="primary"
                icon={<Plus className="size-4" />}
                onClick={() => navigate('/olts/new')}
              >
                Onboard an OLT
              </Button>
            </>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="OLTs" value={counts.total} icon={<Cable className="size-4" />} />
        <StatCard label="Reachable" value={counts.up} tone={counts.up ? 'success' : 'default'} />
        <StatCard label="ONUs discovered" value={counts.onus} />
        <StatCard label="ONUs online" value={counts.online} tone="success" />
      </div>

      {can('olt.manage') && inService.length > 0 && (
        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          {bulkMsg && <span className="mr-auto text-sm text-muted">{bulkMsg}</span>}
          <Button
            icon={<RefreshCw className="size-3.5" />}
            loading={resyncAll.isPending}
            disabled={bulkBusy}
            title={
              (withNtp.length > 0
                ? `Re-reads every OLT in service, and writes the recorded time servers to the ${
                    withNtp.length === 1 ? 'one that has some' : `${withNtp.length} that have some`
                  }`
                : 'Re-reads every OLT in service — inventory, ports and ONUs') + skipping
            }
            onClick={() => {
              /**
               * Reading the fleet needs no confirmation. Writing to it does —
               * and this only writes where an operator has recorded time
               * servers, so the question is asked exactly when there is
               * something to ask about.
               */
              if (
                withNtp.length > 0 &&
                !confirm(
                  `Resync all ${inService.length} OLTs, and write the recorded time servers to ${
                    withNtp.length === 1
                      ? withNtp[0]!.name
                      : `${withNtp.length} of them (${withNtp.map((o) => o.name).join(', ')})`
                  }? Existing servers on each chassis are left in place.${skipping}`,
                )
              ) {
                return;
              }
              setBulkMsg(null);
              resyncAll.mutate();
            }}
          >
            Resync all
          </Button>
          <Button
            icon={<Save className="size-3.5" />}
            loading={saveAll.isPending}
            disabled={bulkBusy}
            onClick={() => {
              if (
                confirm(
                  `Write the running config to flash on all ${inService.length} OLTs? This persists each chassis's current configuration (ZTE write / Huawei save).${skipping}`,
                )
              ) {
                setBulkMsg(null);
                saveAll.mutate();
              }
            }}
          >
            Save / write all
          </Button>
        </div>
      )}

      <Card className={can('olt.manage') && inService.length > 0 ? 'mt-3' : 'mt-5'}>
        {oltsQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : oltsQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={oltsQuery.error} />
          </div>
        ) : olts.length === 0 ? (
          <EmptyState
            icon={<Cable className="size-8" />}
            title="No OLTs yet"
            description="The onboarding wizard walks through preparing the terminal, then verifies it before adding it."
            action={
              can('olt.manage') ? (
                <Button variant="primary" onClick={() => navigate('/olts/new')}>
                  Onboard an OLT
                </Button>
              ) : null
            }
          />
        ) : (
          <Table>
            <THead>
              <th className="th">#</th>
              <th className="th">Status</th>
              <th className="th">Name</th>
              <th className="th">Vendor / model</th>
              <th className="th">Management IP</th>
              <th className="th">PON ports</th>
              <th className="th">ONUs</th>
              {showEventsColumn && <th className="th">Events</th>}
              <th className="th">Uptime</th>
              <th className="th">Last poll</th>
              <th className="th" />
            </THead>
            <TBody>
              {olts.map((olt) => (
                <TRow key={olt.id} onClick={() => navigate(`/olts/${olt.number ?? olt.id}`)}>
                  <td className="td text-sm tabular-nums text-muted">#{olt.number ?? '—'}</td>
                  <td className="td">
                    <Pill state tone={STATUS_TONE[olt.status]} dot>
                      {olt.status}
                    </Pill>
                  </td>
                  <td className="td font-medium">
                    {olt.name}
                    {/*
                      Beside the name rather than in the Status column, which
                      already means something else: `status` is what the last
                      poll found, and on a disabled chassis that poll is however
                      old the decision is. The pill is the answer to "why is
                      this row not moving", and its tooltip says so — a column
                      of its own would spend width on four chassis that almost
                      never have one.
                    */}
                    {olt.disabledAt && (
                      <DisabledPill
                        kind="olt"
                        disabledReason={olt.disabledReason}
                        className="ml-2 align-middle"
                      />
                    )}
                    {armedByOlt.has(olt.id) && (
                      <Pill className="ml-2" tone="warn" dot>
                        auto-authorise armed
                      </Pill>
                    )}
                    {/*
                      A COMMUNICATION failure with the chassis — SNMP or CLI
                      unreachable — and nothing else. It used to be keyed off
                      `last_error`, but that field holds the sweep's notes as
                      often as its failures ("1361 ONUs across 32 ports", "4 ONU
                      state change(s) recorded"), so a chassis that answered fine
                      wore a permanent amber marker for routine fleet churn. That
                      churn belongs in the Events column and the OLT's Events
                      tab, not on a warning triangle. So this is gated on
                      reachability alone — `status === 'down'` on a chassis that
                      is not deliberately disabled — the same signal the bell and
                      the OLT-detail header judge unreachable by. Its tooltip
                      carries the connection error the down sweep recorded; the
                      full note still reads on the Details tab.
                    */}
                    {olt.status === 'down' && !olt.disabledAt && (
                      <span
                        className="ml-2 inline-flex align-text-bottom text-danger"
                        title={
                          olt.lastError
                            ? `Unreachable — ${olt.lastError}`
                            : 'Unreachable — no response over SNMP or CLI'
                        }
                        aria-label={
                          olt.lastError
                            ? `Unreachable: ${olt.lastError}`
                            : 'Unreachable over SNMP or CLI'
                        }
                      >
                        <AlertTriangle className="size-3.5" />
                      </span>
                    )}
                  </td>
                  <td className="td text-muted">
                    {olt.vendor.toUpperCase()}
                    {olt.model ? ` · ${olt.model}` : ''}
                  </td>
                  <td className="td font-mono text-xs">{olt.mgmtIp}</td>
                  <td className="td tabular-nums">{olt.ponPortCount}</td>
                  <td className="td tabular-nums">
                    <span className="text-success">{olt.onuOnlineCount}</span>
                    <span className="text-faint"> / {olt.onuCount}</span>
                  </td>
                  {/*
                    Stops the row's own click, so this cell keeps its tab.

                    Rendered for EVERY row while the column exists, silenced or
                    not: dropping the cell on some rows and not others would
                    shift uptime and last-poll one column left on those rows.
                    A silenced OLT gets a dash instead of a count.
                  */}
                  {showEventsColumn && (
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      {eventNotificationsOn(olt) ? (
                        <EventsCell olt={olt} unseen={unseenByOlt.get(olt.id) ?? 0} />
                      ) : (
                        <span
                          className="text-faint"
                          title="Event notifications are off for this OLT. Its events are still being recorded — open the OLT and use its Events tab, or turn notifications back on from Edit."
                        >
                          —
                        </span>
                      )}
                    </td>
                  )}
                  <td className="td text-muted">{formatUptime(olt.uptimeSeconds)}</td>
                  <td className="td text-muted">{formatRelative(olt.lastPolledAt)}</td>
                  <td className="td text-right" onClick={(e) => e.stopPropagation()}>
                    {can('olt.diagnose') && (
                      /*
                        Both of these reach the chassis — Migration writes to
                        it and Test reads it — and the API refuses a probe on a
                        disabled OLT outright. Greyed together so the row reads
                        as one decision rather than as one broken button beside
                        one working one.
                      */
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          icon={<Shuffle className="size-3.5" />}
                          disabled={Boolean(olt.disabledAt)}
                          title={
                            olt.disabledAt
                              ? `${olt.name} is out of service in SimpleACS. Enable it to move ONUs on it.`
                              : 'ONUs that have turned up here unconfigured but are recorded on another port, card or OLT'
                          }
                          onClick={() => setMigrating(olt)}
                        >
                          Migration
                        </Button>
                        <Button
                          size="sm"
                          icon={<Radar className="size-3.5" />}
                          loading={probe.isPending && probe.variables === olt.id}
                          disabled={Boolean(olt.disabledAt)}
                          title={
                            olt.disabledAt
                              ? `${olt.name} is out of service in SimpleACS, so nothing is read from it. Enable it first.`
                              : undefined
                          }
                          onClick={() => probe.mutate(olt.id)}
                        >
                          Test
                        </Button>
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {migrating && (
        <OnuMigrationDialog olt={migrating} onClose={() => setMigrating(null)} />
      )}
      {smartolt && <MigrateDialog onClose={() => setSmartolt(false)} />}
    </>
  );
}

/**
 * The Events column: how many ONU state changes on this chassis this operator
 * has not looked at, and a way straight to them.
 *
 * `?tab=events` opens the OLT's OWN events tab with the chassis already
 * chosen — the list the old banner told you to go and find, without the
 * finding. Always a link, whether or not anything is new, because "show me
 * what this chassis has been doing" is a question worth asking on a quiet day
 * too.
 */
function EventsCell({ olt, unseen }: { olt: Olt; unseen: number }) {
  return (
    <Link
      to={`/olts/${olt.number ?? olt.id}?tab=events`}
      className="inline-flex rounded-full hover:opacity-80"
      title={
        unseen > 0
          ? `${unseen} ONU state change(s) you have not looked at — open the events for the cause of each`
          : 'Nothing new since you last looked — open the full event history'
      }
    >
      {unseen > 0 ? (
        // Capped so one chassis-wide power cut cannot widen the column for
        // every other row; the tooltip still carries the real figure.
        <Pill tone="warn" dot>{`${Math.min(unseen, 999)}${unseen > 999 ? '+' : ''} new`}</Pill>
      ) : (
        /*
         * Quiet on purpose. A chassis nobody needs to look at must not draw
         * the eye, or the ones that do stop meaning anything — which is how
         * four permanent amber banners came to be ignored in the first place.
         */
        <span className="text-faint tabular-nums">0</span>
      )}
    </Link>
  );
}
